// src/pages/BeneficiaryManagement.js
import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container } from "@mui/material";
import Registerd from "../Images/Registered.svg";
import Reffeared from "../Images/Reffered.svg";
import Addicon from "../Images/Add.svg";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonIcon from "@mui/icons-material/Person";
import Header from "../Header/Header";
import Abhasearch from "../Abhasearch/Abhasearch";
import { useNavigate } from "react-router-dom";
import { CommonContext } from "../../context/CommonContext";
import { apisPath, paths } from "../../utils/path";
import { authHttp } from "../../services/commonHttpService";
import { calculateAge, formatAbhaId, formatAbhaInput, formatDate } from "../../helpers/commonHelper";
import { AuthContext } from "../../context/AuthContext";
import BookAppointmentModal from "../MoComponents/BookAppointmentModal/BookAppointmentModal";
import treatmentIcon from "../Images/Icontreatment.svg";
import ViewIcon from "../../Images/view.svg";

const BeneficiaryManagement = () => {
  const navigate = useNavigate();
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { userRole, facilityId } = useContext(AuthContext);
  const [searchType, setSearchType] = useState("ABHA ID");
  const [searchValue, setSearchValue] = useState("");
  const [openAbhaModal, setOpenAbhaModal] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [referredToDCCCCount, setReferredToDCCCCount] = useState(0);

  const [openBookModal, setOpenBookModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [existingSessionsCount, setExistingSessionsCount] = useState(0);

  // Search options with their corresponding API keys
  const searchOptions = [
    { type: "ABHA ID", icon: <AccountBoxIcon />, apiKey: "abha" },
    { type: "Patient ID", icon: <BadgeIcon />, apiKey: "patient_id" },
    { type: "Mobile Number", icon: <PhoneIphoneIcon />, apiKey: "mobile" },
    { type: "Name", icon: <PersonIcon />, apiKey: "full_name" },
  ];

  // Helper function to get patient status based on role and conditions
  const getPatientStatus = (row) => {
    if (userRole === 5) {
      if (row?.status_action === 3) return "All Treatment Completed";
      if (row?.status_action === 2) return "Ongoing";
      if (row?.status_action === 1) return "Scheduled";
      if (row?.status_action === 0) return "Pending";
      // Any other status_action value
      return "-";
    } else {
      // workflow_id 1, 2, or 17
      if ([1, 2].includes(row?.workflow_id)) return "Registered";

      if ([17].includes(row?.workflow_id)) return "Refered Back from DCCC";
      // workflow_id 3, 4, 5, or 6
      if ([3, 4, 5, 6].includes(row?.workflow_id)) return "Ongoing";
      // workflow_id 7-14
      if (row?.workflow_id >= 7 && row?.workflow_id <= 14) return "Completed";
    }
    return "N/A";
  };

  // Helper function to get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "Ongoing":
        return "warning";
      case "Scheduled":
        return "orange";
      case "Pending":
        return "pending";
      case "Registered":
        return "info";
      case "Completed":
        return "success";
      default:
        return "default";
    }
  };

  // Helper function to get action button details
  const getActionButton = (row) => {
    if (userRole === 5) {
      if (row?.status_action === 2) {
        return {
          label: "Complete Treatment",
          onClick: () => handleInitiateTreatment(row),
          icon: true,
        };
      }
      // if (row?.status_action === 1 || row?.status_action === 9) {
      if (row?.status_action === 1) {
        return {
          label: "Initiate Treatment",
          onClick: () => handleInitiateTreatment(row),
          icon: true,
        };
      }
      if (row?.status_action === 0) {
        return {
          label: "Book Appointment",
          onClick: () => handleBookSlot(row),
          icon: true,
        };
      }
      return {
        label: "Referred to MI",
        onClick: null,
        icon: false,
      };
    } else {
      if ([1, 2, 17].includes(row?.workflow_id)) {
        return {
          label: "Initiate Treatment",
          onClick: () => handleInitiateTreatment(row),
          icon: true,
        };
      }
      if ([3, 4, 5, 6].includes(row?.workflow_id)) {
        return {
          label: "Complete Treatment",
          onClick: () => handleInitiateTreatment(row),
          icon: true,
        };
      }
      if (row?.workflow_id >= 7 && row?.workflow_id <= 14) {
        return {
          label: row?.dccc_name || "DCCC",
          fullLabel: row?.dccc_name || "DCCC",
          onClick: null,
          icon: false,
          showTooltip: true,
          isChip: true,
        };
      }
    }
    return null;
  };

  // Fetch patient list
  const fetchPatientList = async (searchParams = null) => {
    setIsLoading(true);
    try {
      const baseUrl = userRole === 5
        ? apisPath?.moPatientList
        : apisPath?.patientList;

      // Build query parameters
      const queryParams = [];

      if (facilityId) {
        queryParams.push(`facility_id=${facilityId}`);
      }

      if (searchParams) {
        queryParams.push(searchParams);
      }

      const url = queryParams.length > 0
        ? `${baseUrl}?${queryParams.join('&')}`
        : baseUrl;


      const response = await authHttp.get(url);

      if (response?.data) {
        setBeneficiaries(userRole === 5 ? response?.data?.data?.result : response?.data?.data);
        setTotalPatients(userRole === 5 ? response?.data?.data?.result.length : response?.data?.data.length);
      }
    } catch (error) {
      setAlertState((prev) => ({
        ...prev,
        open: true,
        title: error?.response?.data?.message,
        btnTxt: "CLOSE",
        status: "fail",
      }));
      setBeneficiaries([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    if (!searchValue.trim()) {
      fetchPatientList();
      return;
    }

    // Finding the API key for the selected search type
    const selectedOption = searchOptions.find(
      (option) => option.type === searchType
    );
    const apiKey = selectedOption?.apiKey;

    if (!apiKey) return;

    // Building search parameter
    let searchParam = "";

    // Convert to integer if searching by Patient ID
    if (apiKey === "patient_id") {
      const patientId = parseInt(searchValue);
      if (isNaN(patientId)) {
        setAlertState((prev) => ({
          ...prev,
          open: true,
          title: "Please enter a valid Patient ID (numbers only)",
          btnTxt: "CLOSE",
          status: "fail",
        }));
        return;
      }
      searchParam = `${apiKey}=${patientId}`;
    } else {
      searchParam = `${apiKey}=${encodeURIComponent(searchValue.trim())}`;
    }

    fetchPatientList(searchParam);
  };

  // Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle initiate treatment - conditional redirect based on userRole
  const handleInitiateTreatment = (row) => {
    if (userRole === 5) {
      navigate(
        `${paths?.user?.commonPoint}${paths?.user?.treatmentProcedure}`,
        {
          state: {
            patientId: row?.patient_id,
            planned_chemo_cycles: row?.planned_chemo_cycles,
            upcoming_on_min: row?.upcoming_on_min,
            patientName: row?.full_name
          },
        }
      );
    } else {
      navigate(`${paths?.user?.commonPoint}${paths?.user?.treatmentModule}`, {
        state: { patientId: row?.patient_id },
      });
    }
  };

  // Handle view for MI users after refer to DCCC
  const handleReferToDccc = (patientId) => {
    navigate(`${paths?.user?.commonPoint}${paths?.user?.miTreatmentProcedure}`, {
      state: { patientId },
    });
  }

  // Fetch existing sessions count
  const fetchExistingSessionsCount = async (patientId, plannedCycles) => {
    try {
      const response = await authHttp.get(`${apisPath?.getAllChemoSessions}/${patientId}`);
      
      if (response?.data?.status && response?.data?.data) {
        const count = response?.data?.data.length;
        setExistingSessionsCount(count);
        
        // Show alert if sessions >= planned cycles (but still proceed)
        if (count >= plannedCycles) {
          setAlertState((prev) => ({
            ...prev,
            open: true,
            title: "You have booked all the sessions suggested by TCCC.",
            btnTxt: "CLOSE",
            status: "info",
          }));
        }
      }
    } catch (error) {
      // If API fails, default to 0
      setExistingSessionsCount(0);
    }
  };

  // Handle book slot
  const handleBookSlot = async (patient) => {
    setSelectedPatient({
      patient_id: patient?.patient_id,
      full_name: patient?.full_name,
      planned_chemo_cycles: patient?.planned_chemo_cycles || 0,
    });

     // Fetch existing sessions count
    await fetchExistingSessionsCount(patient?.patient_id, patient?.planned_chemo_cycles);

    setOpenBookModal(true);
  };

  // Handle booking success
  const handleBookingSuccess = () => {
    setOpenBookModal(false);
    setSelectedPatient(null);
    fetchPatientList(); // Refresh list
    navigate(
      `${paths?.user?.commonPoint}${paths?.user?.moBeneficiaryManagement}`
    );
  };

  // Fetch patient list on component mount
  useEffect(() => {
    fetchPatientList();
  }, []);

  // Calculate referred to DCCC count
  useEffect(() => {
    if (userRole === 4) {
      const count = beneficiaries.filter(
        (patient) => patient?.workflow_id >= 7 && patient?.workflow_id <= 14
      ).length;
      setReferredToDCCCCount(count);
    }
  }, [beneficiaries, userRole]);

  // Reset search value when search type changes
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setSearchValue("");
  };

  return (
    <>
      <Header showFullHeader={false} />
      <Container>
        {/* Header Section */}

        <h5 className="mb-4 margin-top">Beneficiary Management</h5>

        {/* Stats Section */}
        {userRole === 4 && (
          <div className="row mb-4">
            <div className="col-md-3 col-xs-6 mb-3 mb-md-0">
              <div className="bg-white box-design shadow p-3 d-flex align-items-center">
                <img
                  src={Registerd}
                  alt=""
                  className="me-2"
                  style={{ width: "53px" }}
                />
                <div>
                  <h5 className=" mb-0 text-number">{beneficiaries.length}</h5>
                  <div className="text-tile">Registered Patients</div>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-xs-6 mb-3 mb-md-0">
              <div className="bg-white box-design shadow p-3 d-flex align-items-center">
                <img
                  src={Reffeared}
                  alt=""
                  className="me-2"
                  style={{ width: "53px" }}
                />
                <div>
                  <h5 className="text-number mb-0">{referredToDCCCCount}</h5>
                  <small className="text-tile">Referred to DCCC</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Section */}
        <Typography variant="h6" className="fw-bold mb-3">
          Search Beneficiary
        </Typography>

        <div className="d-flex flex-wrap gap-2 mb-3">
          {searchOptions.map((item) => (
            <Button
              key={item?.type}
              variant="outlined"
              onClick={() => handleSearchTypeChange(item?.type)}
              startIcon={item?.icon}
              className={`search-btn ${searchType === item?.type ? "active" : ""
                }`}
            >
              {item?.type}
            </Button>
          ))}
        </div>

        <div className="d-flex mb-4 flex-wrap gap-2 align-items-center search-bar-container">
          <input
            type="text"
            variant="outlined"
            placeholder={`Enter ${searchType}`}
            size="small"
            value={searchValue}
            onChange={(e) => {
              if (searchType === "ABHA ID") {
                const formatted = formatAbhaInput(e.target.value);
                setSearchValue(formatted);
              } else {
                setSearchValue(e.target.value);
              }
            }}
            onKeyPress={handleKeyPress}
            className="search-bar"
          />

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            className="search-btn"
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>

        {/* Table Section */}
        <div className=" shadow table-design">
          <div className=" d-flex justify-content-between align-items-center">
            <h6 className="mb-0 tile-header">Beneficiary List</h6>

            {/* Only show Add Beneficiary button if user can create */}
            {userRole === 4 && (
              <Button
                variant="contained"
                color="secondary"
                className="rounded-3 mb-2"
                onClick={() => setOpenAbhaModal(true)}
              >
                <img src={Addicon} alt="" />
                Add Beneficiary
              </Button>
            )}
          </div>

          <Abhasearch
            open={openAbhaModal}
            onClose={() => setOpenAbhaModal(false)}
            onSuccess={() => {
              setOpenAbhaModal(false);
              fetchPatientList(); // Refresh list after adding new beneficiary
            }}
          />

          <div className="table-responsive mt-2">
            <table className="table align-middle mb-0 custom-table">
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>ABHA ID</th>
                  <th>Mobile Number</th>
                  <th>Registration</th>
                  {userRole === 5 &&
                    <>
                      <th>Last Treatment</th>
                      <th>Next Treatment</th>
                    </>
                  }
                  <th>Status</th>
                  <th>Action</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {beneficiaries.length > 0 ? (
                  beneficiaries.map((row, i) => {
                    const status = getPatientStatus(row);
                    const actionButton = getActionButton(row);

                    return (
                      <tr key={row?.patient_id || i}>
                        <td>P-{row?.patient_id}</td>
                        <td>{row?.full_name}</td>
                        <td>{calculateAge(row?.dob)}</td>
                        <td>{row?.gender}</td>
                        <td>{formatAbhaId(row?.abha)}</td>
                        <td>{row?.mobile}</td>
                        <td>{userRole === 5 ? formatDate(row?.created_date) : formatDate(row?.created_at)}</td>
                        {userRole === 5 &&
                          <>
                            <td>{row?.completed_on_max ? formatDate(row?.completed_on_max) : "-"}</td>
                            <td>{row?.upcoming_on_min ? formatDate(row?.upcoming_on_min) : "-"}</td>
                          </>
                        }
                        <td>
                          <Chip
                            label={status}
                            color={getStatusColor(status)}
                            size="small"
                            variant="outlined"
                            // className="border-radi"
                            className={`${getStatusColor(status)} border-radi`}
                          />
                        </td>
                        <td>
                          {actionButton ? (
                            actionButton.isChip ? (
                              <Tooltip title={`Referred to ${actionButton?.fullLabel}`} arrow placement="top">
                                <Chip
                                  label={`Referred to ${actionButton?.label}`}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  className="referred-chip"
                                />
                              </Tooltip>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={actionButton.onClick}
                                disabled={!actionButton.onClick}
                                className="cusom-padding"
                              >
                                {actionButton?.icon ? (
                                  <img
                                    src={treatmentIcon}
                                    alt=""
                                    className="treatment-icon"
                                  />
                                ) : null}
                                <span>
                                  {actionButton?.label}
                                </span>
                              </Button>
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </td>
                        <td>
                          {userRole === 5 ? ""
                            : ((row?.workflow_id >= 7 && row?.workflow_id <= 14) &&
                              <img
                                src={ViewIcon}
                                className="viewBtn"
                                alt="view button"
                                onClick={() => handleReferToDccc(row?.patient_id)}
                              />
                            )
                          }
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center p-4">
                      <Typography variant="body2" color="text.secondary">
                        No beneficiaries found
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center margin-bw">
          <Typography variant="body2">
            {beneficiaries.length} results found Showing page 1 of 1
          </Typography>
          <nav>
            <ul className="pagination mb-0">
              <li className="page-item disabled">
                <button className="page-link">Previous</button>
              </li>
              <li className="page-item active">
                <button className="page-link">1</button>
              </li>
              <li className="page-item disabled">
                <button className="page-link">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </Container>

      {selectedPatient && (
        <BookAppointmentModal
          open={openBookModal}
          onClose={() => {
            setOpenBookModal(false);
            setSelectedPatient(null);
            setExistingSessionsCount(0);
          }}
          patientId={selectedPatient?.patient_id}
          patientName={selectedPatient?.full_name}
          planned_chemo_cycles={selectedPatient?.planned_chemo_cycles || 0}
          existingSessionsCount={existingSessionsCount}
          onSuccess={handleBookingSuccess}
        />
      )}
    </>
  );
};

export default BeneficiaryManagement;
